import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/feature/SEOMeta';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex flex-col items-center justify-center pt-16 gap-4">
          <p className="text-foreground-500 text-lg">文章不存在或已被删除</p>
          <Link to="/" className="text-accent-500 hover:text-accent-600 cursor-pointer font-medium text-sm whitespace-nowrap">
            返回首页
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  // Related articles: same category, exclude current
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <SEOMeta
        title={`${article.title} - 51黑料网 | ${article.categoryName}吃瓜爆料`}
        description={article.excerpt}
        keywords={`51黑料网,${article.tags.join(',')},${article.categoryName},吃瓜,爆料,每日大赛`}
        canonicalUrl={`https://51heiliaowang.com/article/${article.id}`}
        ogType="article"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            author: { '@type': 'Person', name: article.author },
            publisher: { '@type': 'Organization', name: '51黑料网', url: 'https://51heiliaowang.com' },
            image: article.imageUrl,
            url: `https://51heiliaowang.com/article/${article.id}`,
            mainEntityOfPage: { '@type': 'WebPage', '@id': `https://51heiliaowang.com/article/${article.id}` },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://51heiliaowang.com' },
              { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://51heiliaowang.com/category/${article.category}` },
              { '@type': 'ListItem', position: 3, name: article.title, item: `https://51heiliaowang.com/article/${article.id}` },
            ],
          },
        ]}
      />

      <Navbar />

      <main>
        {/* Article Hero */}
        <section className="relative h-[450px] md:h-[550px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/60"></div>
          </div>

          <div className="relative z-10 w-full px-6 md:px-10 mt-16">
            {/* Breadcrumb */}
            <nav className="mb-4" aria-label="面包屑导航">
              <ol className="flex items-center flex-wrap gap-2 text-sm text-white/50">
                <li><Link to="/" className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">首页</Link></li>
                <li><i className="ri-arrow-right-s-line text-xs"></i></li>
                <li><Link to={`/category/${article.category}`} className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">{article.categoryName}</Link></li>
                <li><i className="ri-arrow-right-s-line text-xs"></i></li>
                <li className="text-white/70 whitespace-nowrap">正文</li>
              </ol>
            </nav>

            <span className="inline-block px-2.5 py-0.5 rounded-full bg-accent-500/80 text-white text-xs font-medium mb-4 whitespace-nowrap">
              {article.categoryName} · {article.author}
            </span>

            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight max-w-3xl">
              {article.title}
            </h1>
          </div>

          <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
            <p className="font-heading text-6xl md:text-8xl lg:text-9xl font-black text-white/6 leading-none tracking-tighter pl-6 md:pl-10 pb-4 md:pb-6 select-none whitespace-nowrap overflow-hidden">
              独家黑料 · 深度爆料
            </p>
          </div>
        </section>

        {/* Article Content */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="w-full px-6 md:px-10">
            <div className="max-w-3xl mx-auto">
              {/* Meta bar */}
              <div className="flex flex-wrap items-center gap-3 md:gap-4 mb-8 text-xs md:text-sm text-foreground-400">
                <span className="bg-background-50 px-3 py-1 rounded-full border border-background-200/70 whitespace-nowrap">
                  <i className="ri-calendar-line mr-1"></i>
                  {article.date}
                </span>
                <span className="bg-background-50 px-3 py-1 rounded-full border border-background-200/70 whitespace-nowrap">
                  <i className="ri-eye-line mr-1"></i>
                  {article.views >= 10000 ? (article.views / 10000).toFixed(1) + '万' : article.views} 阅读
                </span>
                <span className="bg-background-50 px-3 py-1 rounded-full border border-background-200/70 whitespace-nowrap">
                  <i className="ri-chat-3-line mr-1"></i>
                  {article.comments} 评论
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {article.tags.map((tag) => (
                    <span key={tag} className="bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full text-xs whitespace-nowrap">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Content body */}
              <article className="prose-custom">
                <div
                  className="text-foreground-700 leading-relaxed text-sm md:text-base space-y-4"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </article>

              {/* Stats grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-12 pt-8 border-t border-background-200/70">
                <StatItem value={(article.views / 10000).toFixed(0) + '万+'} label="阅读量" />
                <StatItem value={String(article.comments) + '+'} label="评论数" />
                <StatItem value="98%" label="好评率" />
                <StatItem value="12+" label="相关爆料" />
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-16 md:py-20" aria-labelledby="related-title">
            <div className="w-full px-6 md:px-10">
              <div className="text-center mb-10">
                <h2 id="related-title" className="text-3xl md:text-5xl font-black text-foreground-950 mb-3">
                  更多同类黑料
                </h2>
                <p className="text-sm text-foreground-500">猜你还想看这些爆料</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group cursor-pointer rounded-lg overflow-hidden relative h-[320px] md:h-[360px]"
                  >
                    <img
                      src={ra.imageUrl}
                      alt={ra.title}
                      className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="text-base font-bold text-white leading-snug mb-2 line-clamp-2">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-white/50 line-clamp-1">{ra.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="text-center mt-8">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-secondary-500 text-white font-semibold text-sm hover:bg-secondary-600 cursor-pointer transition-all whitespace-nowrap"
                >
                  查看全部{article.categoryName}爆料
                  <i className="ri-arrow-right-line text-lg"></i>
                </Link>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}

function StatItem({ value, label }: { value: string; label: string }) {
  return (
    <div className="text-center p-3 md:p-4 rounded-lg bg-background-50 border border-background-200/70">
      <div className="text-xl md:text-3xl font-black text-foreground-950 mb-1">{value}</div>
      <div className="text-xs md:text-sm text-foreground-400">{label}</div>
    </div>
  );
}